import { useLocation, useNavigate, useParams } from "react-router-dom"
import { useState, useEffect, useRef } from "react"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Loader2,
  Images,
  Layout,
  AlertCircle,
  ShieldAlert,
  Users,
  FlaskConical,
} from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"
import { tDynamic } from "@/i18n"
import { UserMenu } from "@/features/auth/components/UserMenu"
import { LanguageSwitcher } from "@/components/common/LanguageSwitcher"
import { HistoryTab } from "@/features/history/components/HistoryTab"
import { TemplatesTab } from "@/features/templates/components/TemplatesTab"
import { CommunityTab } from "@/features/community/components/CommunityTab"
import { GeneratorSidebar } from "@/features/generator/components/GeneratorSidebar"
import { MobileSidebarDrawer } from "@/features/generator/components/MobileSidebarDrawer"
import { MobileHeader, WrksLink } from "@/components/layout/MobileHeader"
import { useGeneratorStore } from "@/stores/generatorStore"
import { useAuthStore } from "@/stores/authStore"
import { useHistory } from "@/hooks/useHistory"
import { useAuthMe } from "@/hooks/useAuthMe"
import { useConfig } from "@/hooks/useConfig"
import { useQueueProcessor } from "@/hooks/useQueueProcessor"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { API_BASE_URL } from "@/lib/constants"
import { AuthRequired } from "@/pages/AuthRequired"
import { exitStaging } from "@/lib/staging"

function getTabFromPath(pathname: string): string | null {
  if (pathname === "/my") return "history"
  if (pathname === "/templates" || pathname.startsWith("/templates/")) return "templates"
  if (pathname === "/community") return "community"
  return null // "/" 경로는 기존 로직 사용
}

function getPathFromTab(tab: string): string {
  if (tab === "history") return "/my"
  if (tab === "community") return "/community"
  return "/templates"
}

export function Generator() {
  const { t } = useTranslation()
  const {
    prompt,
    style: selectedStyle,
    aspectRatio: ratio,
    count,
    referenceImages,
    selectedTemplate,
    usageLimitModalOpen,
    ssoTokenExpiredModalOpen,
    safetyErrorModalOpen,
    safetyFlaggedCategories,
    enqueueGeneration,
    setUsageLimitModalOpen,
    setSsoTokenExpiredModalOpen,
    setSafetyErrorModalOpen,
    setSafetyFlaggedCategories,
    requestPromptFocus,
  } = useGeneratorStore()

  const location = useLocation()
  const navigate = useNavigate()
  const { categoryId } = useParams<{ categoryId?: string }>()

  const [activeTab, setActiveTab] = useState<string>("")
  const [isScrolled, setIsScrolled] = useState(false)
  const [authDialogOpen, setAuthDialogOpen] = useState(false)
  const [isValidatingToken, setIsValidatingToken] = useState(true)
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const { isAuthenticated } = useAuthStore()
  const { data: historyData, isLoading: isHistoryLoading } = useHistory(1, 1) // 첫 페이지 1개만 가져와서 확인
  const { data: authMe, isError: authMeError } = useAuthMe()
  const communityHidden = authMe?.menuHidden?.community ?? false
  const { data: config } = useConfig()
  const outageEnabled = config?.outageNotice?.enabled === true
  const outageMessage = config?.outageNotice?.message ?? ''

  // 스테이징은 admin 전용. 라우팅 쿠키는 수동 설정될 수 있으므로, 비admin이거나 인증이
  // 실패한 방문자(STAGING_MODE에서 백엔드가 /auth/me를 403 처리)는 프로덕션으로 되돌린다.
  useEffect(() => {
    if (!config?.isStaging) return
    if (authMeError || (authMe && !authMe.isAdmin)) {
      exitStaging()
    }
  }, [config?.isStaging, authMeError, authMe])

  // 큐 프로세서 (자동으로 pending 아이템 처리)
  const { activeQueueCount, hasProcessingItem, isQueueFull } = useQueueProcessor()

  // 인증 검증: 토큰 유효성 확인 및 hasUsage 갱신
  useEffect(() => {
    const validateAuth = async () => {
      if (!isAuthenticated) {
        setIsValidatingToken(false)
        setAuthDialogOpen(true)
        return
      }

      try {
        const response = await fetch(`${API_BASE_URL}/auth/me`, {
          credentials: 'include',
        })
        if (!response.ok) {
          useAuthStore.getState().logout()
          setAuthDialogOpen(true)
        } else {
          // hasUsage 갱신 (Wrks API에서 최신 값 반영)
          const data = await response.json()
          const currentUser = useAuthStore.getState().user
          if (currentUser && data.hasUsage !== currentUser.hasUsage) {
            useAuthStore.getState().setUser({
              ...currentUser,
              hasUsage: data.hasUsage,
            })
          }
        }
      } catch {
        useAuthStore.getState().logout()
        setAuthDialogOpen(true)
      } finally {
        setIsValidatingToken(false)
      }
    }
    validateAuth()
  }, [isAuthenticated])

  // 탭 변경 핸들러 (URL도 함께 업데이트)
  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab)
    navigate(getPathFromTab(newTab))
  }

  // 카테고리 변경 핸들러 (URL 업데이트)
  const handleCategoryChange = (newCategoryId: string | null) => {
    if (newCategoryId) {
      navigate(`/templates/${newCategoryId}`)
    } else {
      navigate("/templates")
    }
  }

  // 최초 탭 결정: URL 우선, 없으면 히스토리 데이터 기반
  useEffect(() => {
    if (activeTab !== "") return // 이미 탭이 설정되었으면 스킵
    if (isHistoryLoading) return // 로딩 중이면 대기
    if (!historyData) return

    // 1. URL에서 탭 결정 시도
    const urlTab = getTabFromPath(location.pathname)
    if (urlTab) {
      setActiveTab(urlTab)
      return
    }

    // 2. "/" 경로: 기존 로직 (히스토리 유무에 따라 결정)
    const defaultTab = historyData.items.length === 0 ? "templates" : "history"
    setActiveTab(defaultTab)
    // URL도 업데이트 (replace로 히스토리 스택에 추가 안 함)
    navigate(getPathFromTab(defaultTab), { replace: true })
  }, [historyData, isHistoryLoading, activeTab, location.pathname, navigate])

  // URL 변경 시 탭 상태 동기화 (사이드바 등 외부에서 navigate 호출 시)
  useEffect(() => {
    if (activeTab === "") return // 초기 설정 전에는 스킵
    const urlTab = getTabFromPath(location.pathname)
    if (urlTab && urlTab !== activeTab) {
      setActiveTab(urlTab)
    }
  }, [location.pathname, activeTab])

  // 커뮤니티 가드: enterprise 차단 사용자가 /community 로 들어왔거나 community 탭에 있으면 history 로 리다이렉트
  useEffect(() => {
    if (!communityHidden) return
    if (activeTab === "community" || location.pathname === "/community") {
      setActiveTab("history")
      navigate("/my", { replace: true })
    }
  }, [communityHidden, activeTab, location.pathname, navigate])

  const handleGenerate = () => {
    if (!prompt.trim()) return
    if (isQueueFull) {
      toast.error(t('pages.queueFull'), {
        description: t('pages.queueFullDescription'),
      })
      return
    }

    // 큐에 추가
    const queueId = enqueueGeneration({
      prompt,
      style: selectedStyle,
      aspectRatio: ratio,
      count,
      referenceImages,
      templateId: selectedTemplate?.id,
      templateInfo: selectedTemplate ? {
        id: selectedTemplate.id,
        name: selectedTemplate.name,
        description: selectedTemplate.description,
        thumbnailUrl: selectedTemplate.thumbnailUrl,
        nameI18n: selectedTemplate.nameI18n,
        descriptionI18n: selectedTemplate.descriptionI18n,
      } : undefined,
    })

    if (!queueId) {
      toast.error(t('pages.queueAddFailed'), {
        description: t('pages.queueFull'),
      })
      return
    }

    // 생성 시작 시 즉시 '내 이미지' 탭으로 전환 + 스크롤 맨 위로
    if (activeTab !== "history") {
      setActiveTab("history")
      navigate("/my")
    }
    // 스크롤을 맨 위로 (탭 전환 직후 실행되도록 setTimeout 사용)
    setTimeout(() => {
      scrollContainerRef.current?.scrollTo(0, 0)
    }, 0)
  }

  // 토큰 검증 중이면 로딩 표시
  if (isValidatingToken) {
    return (
      <div className="flex h-screen bg-slate-50 items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    )
  }

  // 인증되지 않은 경우 AuthRequired 페이지 표시
  if (authDialogOpen) {
    return <AuthRequired />
  }

  const queueState = { activeQueueCount, hasProcessingItem, isQueueFull }

  return (
    <>
      <div className="flex flex-col lg:flex-row h-screen bg-slate-50 overflow-hidden">
        {/* Mobile Header - lg 미만에서만 표시 */}
        <MobileHeader
          onMenuClick={() => setMobileDrawerOpen(true)}
          className="lg:hidden"
        />

        {/* Mobile Sidebar Drawer */}
        <MobileSidebarDrawer
          open={mobileDrawerOpen}
          onOpenChange={setMobileDrawerOpen}
          showQueueState
          queueState={queueState}
          onGenerate={handleGenerate}
        />

        {/* Desktop Sidebar - lg 이상에서만 표시 */}
        <GeneratorSidebar
          showQueueState
          queueState={queueState}
          onGenerate={handleGenerate}
        />

        {/* Main Content - Tabs */}
        <main className="flex-1 flex flex-col overflow-hidden">
          {activeTab === "" ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : (
          <Tabs value={activeTab} onValueChange={handleTabChange} className="flex flex-col h-full">
            {/* Fixed Header - Tabs + Logout */}
            <div className={`h-14 lg:h-16 px-4 lg:px-8 shrink-0 bg-slate-50 flex items-center transition-shadow z-10 ${isScrolled ? 'shadow-sm' : ''}`}>
              <div className="w-full max-w-5xl mx-auto flex items-center justify-between">
                <TabsList className="w-full lg:w-auto">
                  <TabsTrigger value="history" className="flex-1 lg:flex-initial flex items-center justify-center gap-1.5 lg:gap-2 px-2 lg:px-6">
                    <Images className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('pages.tabMyImages')}</span>
                  </TabsTrigger>
                  <TabsTrigger value="templates" className="flex-1 lg:flex-initial flex items-center justify-center gap-1.5 lg:gap-2 px-2 lg:px-6">
                    <Layout className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('pages.tabTemplates')}</span>
                  </TabsTrigger>
                  {!communityHidden && (
                    <TabsTrigger value="community" className="flex-1 lg:flex-initial flex items-center justify-center gap-1.5 lg:gap-2 px-2 lg:px-6">
                      <Users className="w-4 h-4" />
                      <span className="hidden sm:inline">{t('pages.tabCommunity')}</span>
                    </TabsTrigger>
                  )}
                </TabsList>
                {/* WrksLink + LanguageSwitcher + UserMenu - 데스크톱에서만 표시 (모바일은 MobileHeader에 있음) */}
                <div className="hidden lg:flex items-center gap-2">
                  <WrksLink />
                  <LanguageSwitcher />
                  <UserMenu />
                </div>
              </div>
            </div>

            {/* 스테이징 배너 */}
            {config?.isStaging && (
              <div className="px-4 lg:px-8 shrink-0 bg-slate-50">
                <div className="w-full max-w-5xl mx-auto pb-3">
                  <div className="flex items-center justify-center gap-2 rounded-md border border-amber-300 bg-amber-50 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-amber-700">
                    <FlaskConical className="h-3.5 w-3.5" />
                    {t('pages.stagingBanner')}
                  </div>
                </div>
              </div>
            )}

            {/* 장애 공지 배너 (활성화 시 탭 콘텐츠 위에 노출) */}
            {outageEnabled && (
              <div className="px-4 lg:px-8 shrink-0 bg-slate-50">
                <div className="w-full max-w-5xl mx-auto pb-3">
                  <Alert variant="destructive" className="border-red-200 bg-red-50">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="whitespace-pre-wrap">
                      {outageMessage}
                    </AlertDescription>
                  </Alert>
                </div>
              </div>
            )}

            {/* Scrollable Content */}
            <div
              ref={scrollContainerRef}
              className="flex-1 overflow-y-auto px-4 lg:px-8 pt-0 pb-6"
              onScroll={(e) => setIsScrolled(e.currentTarget.scrollTop > 0)}
            >
              <div className="w-full max-w-5xl mx-auto">
                <TabsContent value="history" className="mt-0">
                  <HistoryTab />
                </TabsContent>

                <TabsContent value="templates" className="mt-0">
                  <TemplatesTab
                    selectedCategoryId={categoryId ?? null}
                    onCategoryChange={handleCategoryChange}
                  />
                </TabsContent>

                {!communityHidden && (
                  <TabsContent value="community" className="mt-0">
                    <CommunityTab />
                  </TabsContent>
                )}
              </div>
            </div>
          </Tabs>
          )}
        </main>
      </div>

      {/* 사용량 한도 초과 모달 */}
      <Dialog open={usageLimitModalOpen} onOpenChange={setUsageLimitModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="space-y-3">
            <DialogTitle className="flex items-center gap-2 text-xl">
              <AlertCircle className="w-6 h-6 text-red-600" />
              {t('modals.usageLimitTitle')}
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground">
              {t('modals.usageLimitBody')}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="mt-4">
            <Button
              onClick={() => setUsageLimitModalOpen(false)}
              className="w-full sm:w-auto"
            >
              {t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SSO 토큰 만료 모달 */}
      <Dialog open={ssoTokenExpiredModalOpen} onOpenChange={setSsoTokenExpiredModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="space-y-3">
            <DialogTitle className="flex items-center gap-2 text-xl">
              <AlertCircle className="w-6 h-6 text-amber-500" />
              {t('modals.ssoExpiredTitle')}
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground">
              {t('modals.ssoExpiredBody')}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="mt-4">
            <Button
              onClick={async () => {
                setSsoTokenExpiredModalOpen(false)
                await useAuthStore.getState().logout()
                window.location.href = '/'
              }}
              className="w-full sm:w-auto"
            >
              {t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 안전 정책 제한 모달 */}
      <Dialog
        open={safetyErrorModalOpen}
        onOpenChange={(open) => {
          setSafetyErrorModalOpen(open)
          if (!open) setSafetyFlaggedCategories([])
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="space-y-3">
            <DialogTitle className="flex items-center gap-2 text-xl">
              <ShieldAlert className="w-6 h-6 text-amber-500" />
              {t('modals.safetyTitle')}
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 text-base text-muted-foreground">
                <p>{t('modals.safetyBody')}</p>
                {safetyFlaggedCategories.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {safetyFlaggedCategories.map((category) => (
                      <span
                        key={category}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"
                      >
                        {tDynamic(`safety.${category}`) ?? category}
                      </span>
                    ))}
                  </div>
                )}
                <ul className="list-disc pl-5 space-y-1 text-sm">
                  <li>{t('modals.safetyTip1')}</li>
                  <li>{t('modals.safetyTip2')}</li>
                </ul>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button
              onClick={() => {
                setSafetyErrorModalOpen(false)
                setSafetyFlaggedCategories([])
                requestPromptFocus()
              }}
              className="w-full sm:w-auto"
            >
              {t('modals.editPrompt')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
